import React, { useState, useEffect } from 'react';
import api from '../api';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { format } from 'date-fns';

export default function Tasks() {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', assignedTo: '', priority: 'medium', dueDate: '' });
  const [bulkForm, setBulkForm] = useState({ title: '', description: '', assignedToList: [], priority: 'medium', dueDate: '' });
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchTasks();
    if (['superadmin', 'manager'].includes(user?.role)) {
      api.get('/users').then(r => setUsers(r.data)).catch(() => {});
    }
  }, [user]);

  const fetchTasks = () => api.get('/tasks').then(r => setTasks(r.data));

  const createTask = async (e) => {
    e.preventDefault();
    try {
      await api.post('/tasks', form);
      toast.success('Task created');
      setShowModal(false);
      setForm({ title: '', description: '', assignedTo: '', priority: 'medium', dueDate: '' });
      fetchTasks();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed');
    }
  };

  const createBulkTasks = async (e) => {
    e.preventDefault();
    if (bulkForm.assignedToList.length === 0) return toast.error('Select at least one employee');
    try {
      const { data } = await api.post('/tasks/bulk', bulkForm);
      toast.success(data.message);
      setShowBulkModal(false);
      setBulkForm({ title: '', description: '', assignedToList: [], priority: 'medium', dueDate: '' });
      fetchTasks();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed');
    }
  };

  const toggleBulkUser = (userId) => {
    setBulkForm(f => ({
      ...f,
      assignedToList: f.assignedToList.includes(userId)
        ? f.assignedToList.filter(id => id !== userId)
        : [...f.assignedToList, userId]
    }));
  };

  const updateStatus = async (id, status) => {
    try {
      await api.put(`/tasks/${id}`, { status });
      fetchTasks();
    } catch (e) {
      toast.error('Failed');
    }
  };

  const tabs = ['all', 'pending', 'in_progress', 'completed'];
  const counts = Object.fromEntries(tabs.map(t => [t, t === 'all' ? tasks.length : tasks.filter(x => x.status === t).length]));
  const filtered = filter === 'all' ? tasks : tasks.filter(t => t.status === filter);

  return (
    <div className="fadein">
      <div className="page-header">
        <div>
          <div className="page-title">Tasks</div>
          <div className="page-subtitle">{tasks.length} total</div>
        </div>
        {['superadmin', 'manager'].includes(user?.role) && (
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-secondary btn-sm" onClick={() => setShowBulkModal(true)}>Bulk Assign</button>
            <button className="btn btn-primary btn-sm" onClick={() => setShowModal(true)}>+ New Task</button>
          </div>
        )}
      </div>

      <div className="tabs">
        {tabs.map(t => (
          <button key={t} className={`tab ${filter === t ? 'active' : ''}`} onClick={() => setFilter(t)}>
            {t.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}
            <span style={{ marginLeft: 5, fontSize: 11, opacity: 0.7 }}>({counts[t]})</span>
          </button>
        ))}
      </div>

      <div className="card">
        {filtered.length === 0 ? (
          <div className="empty"><div className="empty-icon"></div><p>No tasks</p></div>
        ) : (
          <table>
            <thead>
              <tr>
                <th style={{ width: '35%' }}>Task</th>
                <th>Assigned To</th>
                <th>Priority</th>
                <th>Status</th>
                <th>Due Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(t => (
                <tr key={t._id}>
                  <td>
                    <div style={{ fontWeight: 500 }}>{t.title}</div>
                    {t.description && <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 1 }}>{t.description}</div>}
                  </td>
                  <td>
                    <div style={{ fontSize: 13 }}>{t.assignedTo?.name}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-3)', textTransform: 'capitalize' }}>{t.assignedTo?.role}</div>
                  </td>
                  <td><PriorityTag p={t.priority} /></td>
                  <td><span className={`badge badge-${t.status}`}>{t.status.replace('_', ' ')}</span></td>
                  <td style={{ color: 'var(--text-3)', fontSize: 12.5 }}>{t.dueDate ? format(new Date(t.dueDate), 'MMM d, yyyy') : '—'}</td>
                  <td>
                    {t.status !== 'completed' ? (
                      <div style={{ display: 'flex', gap: 5 }}>
                        {t.status === 'pending' && (
                          <button className="btn btn-xs btn-secondary" onClick={() => updateStatus(t._id, 'in_progress')}>Start</button>
                        )}
                        <button className="btn btn-xs btn-success" onClick={() => updateStatus(t._id, 'completed')}>Done</button>
                      </div>
                    ) : (
                      <span style={{ fontSize: 12, color: 'var(--green)' }}>✓ {t.completedAt ? format(new Date(t.completedAt), 'MMM d') : 'Done'}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* New Task Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-head">
              <h2>New Task</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <form onSubmit={createTask}>
                <div className="form-group">
                  <label>Title</label>
                  <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} required placeholder="Task title" />
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <textarea rows={2} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Optional" style={{ resize: 'vertical' }} />
                </div>
                <div className="form-group">
                  <label>Assign To</label>
                  <select value={form.assignedTo} onChange={e => setForm(f => ({ ...f, assignedTo: e.target.value }))} required>
                    <option value="">Select employee</option>
                    {users.map(u => <option key={u._id} value={u._id}>{u.name} ({u.role})</option>)}
                  </select>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div className="form-group">
                    <label>Priority</label>
                    <select value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}>
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Due Date</label>
                    <input type="date" value={form.dueDate} onChange={e => setForm(f => ({ ...f, dueDate: e.target.value }))} />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                  <button className="btn btn-primary" type="submit" style={{ flex: 1 }}>Create Task</button>
                  <button className="btn btn-secondary" type="button" onClick={() => setShowModal(false)}>Cancel</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Assign Modal */}
      {showBulkModal && (
        <div className="modal-overlay" onClick={() => setShowBulkModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 520 }}>
            <div className="modal-head">
              <h2>Bulk Assign Task</h2>
              <button className="modal-close" onClick={() => setShowBulkModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <form onSubmit={createBulkTasks}>
                <div className="form-group">
                  <label>Title</label>
                  <input value={bulkForm.title} onChange={e => setBulkForm(f => ({ ...f, title: e.target.value }))} required placeholder="Task title" />
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <textarea rows={2} value={bulkForm.description} onChange={e => setBulkForm(f => ({ ...f, description: e.target.value }))} placeholder="Optional" style={{ resize: 'vertical' }} />
                </div>
                <div className="form-group">
                  <label>Assign To (select multiple)</label>
                  <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--r)', padding: '8px 10px', maxHeight: 180, overflowY: 'auto', background: 'var(--surface)' }}>
                    {users.length === 0 ? (
                      <div style={{ color: 'var(--text-3)', fontSize: 13 }}>No employees found</div>
                    ) : users.map(u => (
                      <label key={u._id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', cursor: 'pointer', fontSize: 13 }}>
                        <input
                          type="checkbox"
                          checked={bulkForm.assignedToList.includes(u._id)}
                          onChange={() => toggleBulkUser(u._id)}
                          style={{ width: 'auto', margin: 0 }}
                        />
                        <span>{u.name}</span>
                        <span style={{ color: 'var(--text-3)', fontSize: 11, textTransform: 'capitalize' }}>({u.role})</span>
                      </label>
                    ))}
                  </div>
                  {bulkForm.assignedToList.length > 0 && (
                    <div style={{ fontSize: 12, color: 'var(--accent)', marginTop: 4 }}>
                      {bulkForm.assignedToList.length} employee{bulkForm.assignedToList.length > 1 ? 's' : ''} selected
                    </div>
                  )}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div className="form-group">
                    <label>Priority</label>
                    <select value={bulkForm.priority} onChange={e => setBulkForm(f => ({ ...f, priority: e.target.value }))}>
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Due Date</label>
                    <input type="date" value={bulkForm.dueDate} onChange={e => setBulkForm(f => ({ ...f, dueDate: e.target.value }))} />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                  <button className="btn btn-primary" type="submit" style={{ flex: 1 }}>
                    Assign to {bulkForm.assignedToList.length || 0} Employee{bulkForm.assignedToList.length !== 1 ? 's' : ''}
                  </button>
                  <button className="btn btn-secondary" type="button" onClick={() => setShowBulkModal(false)}>Cancel</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PriorityTag({ p }) {
  const c = { high: 'var(--red)', medium: 'var(--amber)', low: 'var(--green)' };
  return <span style={{ fontSize: 12, fontWeight: 600, color: c[p] || c.medium }}>{p}</span>;
}
